import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Layers, 
  LayoutDashboard, 
  Image, 
  Wand2, 
  Sparkles,
  Camera, 
  Palette, 
  Workflow, 
  Settings, 
  Sun, 
  Moon,
  LogOut,
  User,
  ChevronDown,
  Menu,
  X,
  Images,
  Shield
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = () => {
  const { user, logout, refreshUser, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminTesterMode, setAdminTesterMode] = useState(false);

  // Check admin access on mount
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
        setAdminTesterMode(response.data.admin_tester_mode);
      } catch (error) {
        setIsAdmin(false);
        setAdminTesterMode(false);
      }
    };
    checkAdminAccess();
  }, [token]);

  const navItems = [
    { path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard' },
    { path: '/dashboard/image-to-prompt', icon: <Image className="w-5 h-5" />, label: 'Image to Prompt' },
    { path: '/dashboard/prompt-enhancer', icon: <Wand2 className="w-5 h-5" />, label: 'Prompt Enhancer' },
    { path: '/dashboard/generate', icon: <Sparkles className="w-5 h-5" />, label: 'Image Generator' },
    { path: '/dashboard/mockup', icon: <Camera className="w-5 h-5" />, label: 'Mockup Generator' },
    { path: '/dashboard/background', icon: <Palette className="w-5 h-5" />, label: 'Background Gen' },
    { path: '/dashboard/workflow', icon: <Workflow className="w-5 h-5" />, label: 'Workflow' },
    { path: '/dashboard/gallery', icon: <Images className="w-5 h-5" />, label: 'Gallery' },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-background" />
            </div>
            <span className="font-semibold text-xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MockupPro
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Settings Link */}
        <div className="p-4 border-t border-border">
          <Link
            to="/dashboard/settings"
            className={`sidebar-link ${isActive('/dashboard/settings') ? 'active' : ''}`}
            data-testid="nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </Link>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border z-50">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <Link to="/dashboard" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
                  <Layers className="w-5 h-5 text-background" />
                </div>
                <span className="font-semibold text-xl">MockupPro</span>
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <nav className="p-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
              <Link
                to="/dashboard/settings"
                className={`sidebar-link ${isActive('/dashboard/settings') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Settings className="w-5 h-5" />
                <span>Settings</span>
              </Link>
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="glass-header sticky top-0 z-40 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h1 
                className="text-lg font-semibold hidden sm:block"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {navItems.find(item => isActive(item.path))?.label || 'Settings'}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Theme Toggle */}
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="dashboard-theme-toggle">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <span className="hidden sm:inline">{user?.name || user?.first_name || user?.email?.split('@')[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Shield className="w-4 h-4 mr-2 text-red-500" />
                        Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Email Verification Banner */}
        <EmailVerificationBanner />

        {/* Page Content */}
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
